import { normaliseItemName } from './normaliseItemName.ts';
import type { Suggestion } from './itemsApi.ts';

/**
 * Narrowing the remembered names down to what somebody is typing.
 *
 * **This runs on the phone, over a pool the server has already sent.** The suggestions
 * endpoint returns the household's ranked history rather than the eight chips that fit
 * above the add box, precisely so that this can be a filter and not a request: a `?q=`
 * would put a round trip between every keystroke, and would leave the box dead in a shop
 * with no signal — which is where this app is most used and the whole reason the offline
 * queue exists. It is the same call `aisleFor.ts` makes about classification, for the same
 * reasons.
 *
 * Pure and DOM-free on purpose, like `aisleFor.ts` and `imagePlan.ts`: this is the part
 * worth having tests for, and none of it should need a browser to run.
 */

/** The combining marks NFD leaves behind, which is how the accents come off. */
const COMBINING_MARKS = /[\u0300-\u036f]/g;

/**
 * Letters with a stroke through them, which NFD does not touch.
 *
 * Decomposition only ever splits a character into a base plus a mark, and `ł` has no such
 * decomposition — it is a letter in its own right, not an `l` wearing an accent. Without
 * this "jablka" finds nothing, which on a phone keyboard with no Polish layout is most of
 * the times anybody types it.
 */
const STROKED = new Map([
  ['ł', 'l'],
  ['đ', 'd'],
  ['ø', 'o'],
]);

/** Built from the map so the two cannot drift apart. */
const STROKED_PATTERN = new RegExp(`[${[...STROKED.keys()].join('')}]`, 'g');

/**
 * The form names are compared in while searching.
 *
 * `normaliseItemName` first, so casing and stray spacing are already gone, then the
 * accents. Deliberately **not** a change to `normaliseItemName` itself: that function
 * mirrors `ListItem.Normalise` on the server exactly, and the two agreeing is what makes
 * "you already have that" and the chip grouping the same question. Folding is a search
 * concern, and a search concern only — nothing keyed on it is ever stored or sent.
 */
export function foldForSearch(name: string): string {
  return normaliseItemName(name)
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(STROKED_PATTERN, (character) => STROKED.get(character) ?? character);
}

/**
 * The names to offer for what has been typed so far.
 *
 * With an empty box this is simply the head of the pool — the chips the row has always
 * shown, in the ranking the server sent them in. With something typed it is the names that
 * **start** with it, either outright or at a word boundary: "mil" finds `Milk` and
 * `Oat milk`, "oat mi" finds `Oat milk`.
 *
 * Mid-word substrings deliberately do not match. Nobody types the middle of a word, and
 * admitting them would sit `Buttermilk` under "mil" in between the two things the person
 * actually meant — on a row where every chip has to earn its place.
 *
 * Whole-name matches come before word matches; inside each band the pool's own order is
 * kept, and that order is the frequency-and-recency ranking, so the household's staples
 * stay at the front without this function knowing anything about scoring.
 */
export function matchSuggestions(
  pool: readonly Suggestion[],
  query: string,
  limit: number,
): Suggestion[] {
  const wanted = foldForSearch(query);

  if (wanted.length === 0) {
    return pool.slice(0, limit);
  }

  const startsWithName: Suggestion[] = [];
  const startsWithWord: Suggestion[] = [];

  for (const suggestion of pool) {
    const folded = foldForSearch(suggestion.name);

    if (folded.startsWith(wanted)) {
      startsWithName.push(suggestion);
      continue;
    }

    // Folded names are single-space separated, so this is every word boundary in one pass.
    if (folded.split(' ').some((word) => word.startsWith(wanted))) {
      startsWithWord.push(suggestion);
    }
  }

  return [...startsWithName, ...startsWithWord].slice(0, limit);
}
